import React, { useEffect, useMemo, useState } from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { Box, IconButton, Typography, Chip, Tooltip, FormControlLabel, Switch, Button } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import { tableConfig } from '../../../../commons/table/config/tableConfig';
import moment from 'moment';
import AuditarTrasladoDialog from './AuditarTrasladoDialog';
import { useDispatch } from 'react-redux';
import { autorizarTramosBloque } from '../../../../../actions/traslados';
import snackbarUtils from '../../../../../utils/snackbarUtils';

const PendientesGSTable = ({ data = [], idReferenteUsuario, onRefresh, isLoading, onVisualizarSiniestro, onVisualizarTraslado }) => {
  const [auditData, setAuditData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [soloMisSiniestros, setSoloMisSiniestros] = useState(Boolean(idReferenteUsuario));
  const dispatch = useDispatch();

  useEffect(() => {
    if (idReferenteUsuario) {
      setSoloMisSiniestros(true);
    }
  }, [idReferenteUsuario]);

  const filteredData = useMemo(() => {
    if (soloMisSiniestros && idReferenteUsuario) {
      return (data || []).filter((t) => t.idReferente === idReferenteUsuario);
    }
    return data || [];
  }, [data, soloMisSiniestros, idReferenteUsuario]);

  const authorizeSelected = (tramos, table) => {
    const idTramos = tramos.map((tramo) => tramo.idTramo);
    setExporting(true);
    dispatch(autorizarTramosBloque(idTramos, null, true))
      .then(() => {
        table.resetRowSelection();
        onRefresh();
        snackbarUtils.success("Tramos autorizados correctamente.");
      })
      .catch(() => {
        snackbarUtils.error("Ocurri un error al autorizar en bloque.");
      })
      .finally(() => setExporting(false));
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'idTraslado',
        header: 'Nro Traslado',
        size: 80,
      },
      {
        accessorKey: 'idTramo',
        header: 'Nro Tramo',
        size: 80,
        Cell: ({ cell }) => <b>{cell.getValue()}</b>,
      },
      {
        id: 'nroOrden',
        header: 'Tramo',
        size: 60,
        Cell: ({ row }) => `${row.original.nroOrden} de ${row.original.cantidadTramos}`,
      },
      {
        accessorKey: 'esRetorno',
        header: 'Sentido',
        size: 60,
        Cell: ({ row }) => {
          const isRetorno = row.original.esRetorno;
          return (
            <Chip
              label={isRetorno ? 'Vuelta' : 'Ida'}
              size="small"
              sx={{
                backgroundColor: isRetorno ? '#fff3e0' : '#e3f2fd',
                color: isRetorno ? '#e65100' : '#1565c0',
                fontSize: '0.7rem',
                height: '20px',
              }}
            />
          );
        }
      },
      {
        accessorKey: 'trabajador',
        header: 'Trabajador',
      },
      {
        accessorKey: 'siniestro',
        header: 'Siniestro',
        size: 50,
      },
      {
        accessorKey: 'origen',
        header: 'Origen',
        muiTableBodyCellProps: {
          sx: { whiteSpace: 'normal', wordWrap: 'break-word', maxWidth: '210px' },
        },
      },
      {
        accessorKey: 'destino',
        header: 'Destino',
        muiTableBodyCellProps: {
          sx: { whiteSpace: 'normal', wordWrap: 'break-word', maxWidth: '210px' },
        },
      },
      {
        accessorKey: 'fechaTraslado',
        header: 'Fecha',
        size: 50,
        Cell: ({ cell }) =>
          cell.getValue() ? moment(cell.getValue()).format('DD/MM/YYYY') : '-',
      },
      {
        accessorKey: 'horaTraslado',
        header: 'Hora',
        size: 30,
      },
      {
        accessorKey: 'dni',
        header: 'DNI',
        size: 50,
      },
      {
        accessorKey: 'gestor',
        header: 'Gestor',
      },
      {
        accessorKey: 'transporte',
        header: 'Transporte',
      },
      {
        accessorKey: 'alarmas',
        header: 'Alarmas',
        muiTableBodyCellProps: {
          sx: { whiteSpace: 'normal', wordWrap: 'break-word', maxWidth: '230px' },
        },
        Cell: ({ cell }) => {
          const alarmas = cell.getValue();
          if (!alarmas || alarmas.length === 0) {
            return <Typography variant="caption" color="textSecondary">Sin alarmas</Typography>;
          }
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              {alarmas.map((alarma, index) => (
                <Chip
                  key={index}
                  icon={<WarningAmberIcon style={{ fontSize: '1rem', color: '#f57f17' }} />}
                  label={alarma}
                  size="small"
                  sx={{
                    backgroundColor: '#fff9c4',
                    color: '#f57f17',
                    fontWeight: '500',
                    fontSize: '0.7rem',
                    height: 'auto',
                    py: 0.5,
                    '& .MuiChip-label': {
                      display: 'block',
                      whiteSpace: 'normal',
                    }
                  }}
                />
              ))}
            </Box>
          );
        },
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    ...tableConfig,
    columns,
    data: filteredData,
    state: { isLoading },
    enableRowActions: true,
    enableRowSelection: true,
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: '0.5rem' }}>
        <Tooltip title="Visualizar Siniestro" placement="top">
          <IconButton size="small" onClick={() => onVisualizarSiniestro(row.original)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Visualizar Traslado Completo" placement="top">
          <IconButton size="small" onClick={() => onVisualizarTraslado(row.original)}>
            <LocalShippingIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Auditar" placement="top">
          <IconButton color="primary" size="small" onClick={() => setAuditData(row.original)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    renderTopToolbarCustomActions: ({ table }) => {
      const handleAutorizarBloque = () => {
        const tramos = table.getSelectedRowModel().flatRows.map(r => r.original);
        if (tramos.some(t => t.posibleNegativo)) {
          snackbarUtils.warning("No se pueden autorizar tramos con Posible Negativo en bloque.");
          return;
        }

        authorizeSelected(tramos, table);
      };

      return (
        <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <Typography variant="h6">Tramos Pendientes de Autorización</Typography>
          <IconButton onClick={onRefresh} title="Refrescar">
            <RefreshIcon />
          </IconButton>
          {Boolean(idReferenteUsuario) && (
            <FormControlLabel
              control={
                <Switch
                  checked={soloMisSiniestros}
                  onChange={(e) => setSoloMisSiniestros(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Solo mis siniestros
                </Typography>
              }
              sx={{ ml: 1 }}
            />
          )}
          {table.getIsSomeRowsSelected() || table.getIsAllRowsSelected() ? (
            <Button color="success" variant="contained" onClick={handleAutorizarBloque} size="small" disabled={exporting}>
              Autorizar en bloque
            </Button>
          ) : null}
        </Box>
      );
    },
  });

  return (
    <>
      <MaterialReactTable table={table} />
      {auditData && (
        <AuditarTrasladoDialog
          open={!!auditData}
          onClose={() => setAuditData(null)}
          data={auditData}
          rol="gs"
        />
      )}
    </>
  );
};

export default PendientesGSTable;

